/**
 * Servicio para cálculos de boleta de pago
 * Recibe todos los datos organizados para que implementes tu lógica compleja
 */

import { conceptoVaABoletaPlame } from '../utils/ajusteConceptoDestino';
import { esDescuentoVistaQueReduceBIO } from '../utils/pagoJornalPlanilla';
import {
  aportesLegalesConstruccion,
  calcularRemuneracionPeriodoConstruccion,
  completarCatalogoBoletaConstruccion,
  resolverRegistroAfpDesdeContexto,
} from './calculoRemuneracionConstruccionCivil';
import construccionCivilService from './construccionCivilService';
import { codigoPlameParaDeclaracionRem } from '../utils/horasExtrasConstruccionCivil';

function ingresosParaBoletaPlame(conceptosUsuario) {
  return (conceptosUsuario?.ingresos || []).filter((c) => conceptoVaABoletaPlame(c));
}

function descuentosParaBoletaPlame(conceptosUsuario) {
  return (conceptosUsuario?.descuentos || []).filter((c) => conceptoVaABoletaPlame(c));
}

/** Afectaciones PLAME: el API puede traer flags en raíz o bajo `afectaciones`. */
function obtenerAfectacionPlame(concepto, clave) {
  if (!concepto) return false;
  if (concepto[clave] !== undefined && concepto[clave] !== null) return !!concepto[clave];
  return !!(concepto.afectaciones && concepto.afectaciones[clave]);
}

/**
 * Bonificación especial trabajo agrario (PLAME 0314, Ley 31110 – BETA).
 * Regla solicitada: 30% del sueldo mínimo vigente (M). La fórmula en BD puede usar M y/o R.
 */
function valorBonificacionAgraria0314(datosAPIs, regimenNombre, sueldoBasicoReferencia = 0) {
  const M = Number(
    datosAPIs?.sueldoMinimo?.monto ??
      datosAPIs?.informacionGeneral?.sueldo_minimo?.monto ??
      0
  );
  const R = Number(sueldoBasicoReferencia) || 0;
  if (M <= 0) return 0;

  const regimen = (datosAPIs.regimenesLaborales || []).find((r) => r.nombre === regimenNombre);
  const formulas = datosAPIs.formulas || [];
  const f = regimen
    ? formulas.find((x) => String(x.codigo) === '0314' && Number(x.id_tipo) === Number(regimen.id))
    : null;
  let expr = f?.formula || 'M*30%';
  let calculado = String(expr).replace(/\bM\b/g, String(M));
  calculado = calculado.replace(/\bR\b/g, String(R));
  calculado = calculado.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
  try {
    const limpio = calculado.replace(/\s/g, '');
    if (/^[0-9+\-*/.()]+$/.test(limpio)) {
      return Math.round(eval(calculado) * 100) / 100;
    }
  } catch (e) {
    console.warn('⚠️ No se evaluó fórmula 0314:', e);
  }
  return Math.round(M * 0.3 * 100) / 100;
}

/**
 * Fórmula por código PLAME y régimen: evita fallar si id_tipo viene como string desde la API,
 * si el régimen no tiene filas en `formulas`, o si el seeder solo cargó los primeros regímenes.
 * Orden: régimen actual → Régimen General (PRIVAD) → textoPorDefecto.
 */
function obtenerFormulaOMenos(formulas, regimenesLaborales, idRegimenLaboral, codigoPlame, textoPorDefecto = null) {
  const lista = Array.isArray(formulas) ? formulas : [];
  const rid = idRegimenLaboral != null ? Number(idRegimenLaboral.id) : NaN;
  const cod = String(codigoPlame);
  const match = lista.find((f) => Number(f.id_tipo) === rid && String(f.codigo) === cod);
  if (match?.formula) return match.formula;
  const rg = (regimenesLaborales || []).find(
    (r) => r.codigo === 'PRIVAD' || r.codigo === 'RG' || r.nombre === 'Régimen General'
  );
  if (rg && Number(rg.id) !== rid) {
    const m2 = lista.find((f) => Number(f.id_tipo) === Number(rg.id) && String(f.codigo) === cod);
    if (m2?.formula) return m2.formula;
  }
  if (textoPorDefecto != null && textoPorDefecto !== '') {
    console.warn(`⚠️ Fórmula PLAME ${cod} no encontrada para régimen id=${rid}; usando respaldo: ${textoPorDefecto}`);
    return textoPorDefecto;
  }
  return null;
}

/** Clasifica el régimen para reglas de boleta (nombres nuevos y códigos PRIVAD, MIPYM, etc.). */
function clasificarRegimenBoleta(regimenNombre, regimenesLaborales = []) {
  const nombre = String(regimenNombre || '').trim();
  const reg = (regimenesLaborales || []).find((r) => r.nombre === nombre);
  const cod = String(reg?.codigo || '').toUpperCase();
  const incluye = (...textos) => textos.some((t) => nombre.toLowerCase().includes(String(t).toLowerCase()));

  if (['CONSU', 'CONSTRUCCION'].includes(cod) || incluye('Construcción Civil')) {
    return 'construccion_civil';
  }
  if (['PRIVAD', 'MINGEN', 'RG', 'DOMES', 'PESQU'].includes(cod) || nombre === 'Régimen General' || incluye('Minero General', 'Hogar', 'Pesquero')) {
    return 'general';
  }
  if (['MIPYM', 'MINMIC'].includes(cod) || incluye('Microempresa', 'Micro')) {
    return 'mype_micro';
  }
  if (['MYPE', 'MINMYP', 'MYPP'].includes(cod) || incluye('Pequeña Empresa', 'Pequeña')) {
    return 'mype_pequena';
  }
  if (cod === 'AGRI' || nombre === 'Régimen Agrario') {
    return 'agrario';
  }
  if (cod === 'FORMA' || incluye('Formación Laboral', 'Formacion Laboral')) {
    return 'formacion';
  }
  return null;
}

function fechaHoyIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Día laborable de referencia para la plantilla diaria en ficha de empleado (evita domingo/sábado en 0). */
function diaReferenciaConsuFormularioEmpleado(metadatosOrigen) {
  const origen = String(metadatosOrigen?.origen || '');
  const esFormularioEmpleado = /formulario_empleado|edicion_empleado|edicion_cambio_consu|creacion_empleado/.test(
    origen
  );
  const d = new Date();
  if (esFormularioEmpleado) {
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() - 1);
    }
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parametroConsuVigente(parametros, categoria, dia) {
  return (parametros || [])
    .filter((p) => {
      if (String(p.categoria) !== String(categoria)) return false;
      const desde = String(p.vigente_desde || '').slice(0, 10);
      const hasta = p.vigente_hasta ? String(p.vigente_hasta).slice(0, 10) : '9999-12-31';
      return desde && dia >= desde && dia <= hasta;
    })
    .sort((a, b) => String(b.vigente_desde).localeCompare(String(a.vigente_desde)))[0] || null;
}

function resolverConceptoPlame(lista, codigo, descripcion) {
  const hallado = (lista || []).find(
    (c) => String(c.codigo) === String(codigo) || String(c.codigo_plame) === String(codigo)
  );
  if (hallado) return hallado;
  return { codigo, codigo_plame: codigo, descripcion: descripcion || codigo };
}

function mapearLineasConsuABoleta(items, conceptosPlame) {
  return (items || []).map((item) => {
    const base = item.base_calculo ?? item.base ?? null;
    const codigoPlame = codigoPlameParaDeclaracionRem(item) || item.codigoPlame || item.codigo;
    return {
      id_concepto: resolverConceptoPlame(
        conceptosPlame,
        codigoPlame,
        item.concepto || item.descripcion
      ),
      valor: Number(item.monto || item.resultado || 0),
      base,
      base_calculo: base,
      formula: item.formula,
    };
  });
}

async function armarBoletaDiariaConstruccion(datosCompletos, metadatosOrigen) {
  const consu = datosCompletos.datosAPIs?.construccionCivil || {};
  const ficha = consu.ficha || {
    categoria: 'operario',
    modalidad_beneficios: 'sin_beneficios',
    afiliacion_salud: 'essalud',
  };
  const dia = diaReferenciaConsuFormularioEmpleado(metadatosOrigen) || fechaHoyIso();
  let parametros = consu.parametros || [];
  if (!parametros.length) {
    const res = await construccionCivilService.parametrosGlobales();
    parametros = res.success ? (res.data || []) : [];
  }
  const param = consu.parametro || parametroConsuVigente(parametros, ficha.categoria, dia);
  if (!param) {
    return {
      success: false,
      error: 'No hay jornal vigente para la categoría CONSU. Cárgalo en Configuración del sistema.',
      data: null,
    };
  }

  const tareos = [{
    fecha: dia,
    horas_ordinarias: 8,
    horas_jornada: 8,
    tipo_evento: 'asistencia',
  }];
  const conceptosPlame = datosCompletos.datosAPIs?.conceptosPlame || [];
  const plameConsu = {};
  (consu.plame || []).forEach((row) => {
    if (row?.concepto_clave) plameConsu[row.concepto_clave] = row;
  });
  const calculo = calcularRemuneracionPeriodoConstruccion({
    fechaInicio: dia,
    fechaFin: dia,
    parametro: param,
    ficha,
    tareos,
    plame: plameConsu,
    conceptosPlame,
  });
  if (calculo.error) {
    return { success: false, error: calculo.error, data: null };
  }

  const afpRegistro = resolverRegistroAfpDesdeContexto({
    afpNombre: datosCompletos.datosSalariales?.afp,
    afpId: datosCompletos.datosSalariales?.afpId ?? datosCompletos.datosSalariales?.afp_id,
    afpObjeto: datosCompletos.datosEmpleado?.afp,
    afps: datosCompletos.datosAPIs?.afps,
  });
  const legales = aportesLegalesConstruccion({
    bases: calculo.bases,
    parametro: param,
    sistemaPensionario: datosCompletos.datosSalariales?.sistemaPensionario,
    afpRegistro,
    tipoAfp: datosCompletos.datosSalariales?.tipoAfp,
    fechaNacimiento: datosCompletos.datosEmpleado?.fechaNacimiento,
    plame: plameConsu,
    conceptosPlame,
  });
  const plame = conceptosPlame;
  const plameConsuLista = consu.plame || Object.values(plameConsu);

  const conceptosPlantilla = completarCatalogoBoletaConstruccion(
    {
      ingresos: calculo.ingresos || [],
      descuentos: [...(calculo.descuentos || []), ...(legales.descuentos || [])],
      aportes: [...(calculo.aportes || []), ...(legales.aportes || [])],
    },
    {
      sinTareos: false,
      sistemaPensionario: datosCompletos.datosSalariales?.sistemaPensionario,
      conceptosPlame,
      plameConsu: plameConsuLista,
    }
  );

  return {
    success: true,
    data: {
      mensaje: 'Boleta CONSU diaria: jornal del día. En la planilla se multiplica por los días del periodo.',
      origen: metadatosOrigen,
      dataIngresos: mapearLineasConsuABoleta(conceptosPlantilla.ingresos, plame),
      dataDescuentos: mapearLineasConsuABoleta(conceptosPlantilla.descuentos, plame),
      dataAportes: mapearLineasConsuABoleta(conceptosPlantilla.aportes, plame),
      construccionCivil: { bases: calculo.bases, parametro: param, detalleDias: calculo.detalleDias },
    },
    message: 'Servicio ejecutado exitosamente',
  };
}

const CODIGOS_REGIMEN_MINERO = ['MINGEN', 'MINMIC', 'MINMYP'];
const EXTRA_APORTE_AFP_REGIMEN_MINERO = 2;
const PORCENTAJE_APORTE_MINERO_0616 = 0.5;

/** Régimen minero: 2% adicional del trabajador en PLAME 0611 (no en 0608). */
function esRegimenMinero(regimenNombre, regimenesLaborales = []) {
  const reg = (regimenesLaborales || []).find((r) => r.nombre === regimenNombre);
  const cod = String(reg?.codigo || '').toUpperCase();
  if (CODIGOS_REGIMEN_MINERO.includes(cod)) return true;

  const nombre = String(regimenNombre || '').toLowerCase();
  return nombre.includes('régimen minero') || nombre.includes('regimen minero');
}

/** Base de cálculo AFP (misma lógica que descuento PLAME 0608). */
function calcularBaseAfp0608(dataIngresos = [], totalDescuentosBIO = 0) {
  let base = 0;
  dataIngresos.forEach((ingreso) => {
    const afectaSistemaPrivado =
      ingreso.id_concepto?.afectaciones?.sistema_privado_pension ||
      ingreso.id_concepto?.sistema_privado_pension;
    if (afectaSistemaPrivado) {
      base += Number(ingreso.valor) || 0;
    }
  });
  return Math.max(0, base - (Number(totalDescuentosBIO) || 0));
}

/** Descuentos que reducen base ONP/AFP/EsSalud: inasistencias, tardanzas, 0707 y dominical jornal. */
function obtenerTotalDescuentosBIO(descuentosUsuario = []) {
  const codigosBIO = ['0705', '0704', '0707'];
  let total = 0;
  (descuentosUsuario || []).forEach((descuento) => {
    const codigo = String(
      descuento.codigoPlame || descuento.codigo_plame || descuento.codigo || ''
    );
    if (codigosBIO.includes(codigo) || esDescuentoVistaQueReduceBIO(descuento)) {
      total += parseFloat(descuento.monto || descuento.valor || 0) || 0;
    }
  });
  return total;
}

function buscarConceptoPorCodigoPlame(datosAPIs, codigo) {
  const cod = String(codigo);
  return (
    (datosAPIs.conceptosPlame || []).find((c) => c.codigo === cod || c.codigo_plame === cod) ||
    (datosAPIs.conceptosAportes || []).find((c) => c.codigo === cod || c.codigo_plame === cod)
  );
}

function agregarAporteUsuario(dataAportes, datosAPIs, aporte, codigosExcluidos = []) {
  const codigoPlame = String(aporte.codigoPlame || aporte.codigo_plame || aporte.codigo || '');
  const monto = parseFloat(aporte.monto);
  if (!codigoPlame || Number.isNaN(monto) || monto <= 0) return;
  if (codigosExcluidos.includes(codigoPlame)) return;

  const dataAporte = buscarConceptoPorCodigoPlame(datosAPIs, codigoPlame);
  if (!dataAporte) {
    console.warn(`⚠️ Concepto PLAME ${codigoPlame} no encontrado para aporte de usuario`);
    return;
  }

  dataAportes.push({
    id_concepto: dataAporte,
    valor: monto,
  });
}

/** Descuento trabajador régimen minero (PLAME 0611): 2% sobre base AFP. */
function valorDescuentoMinero0611(dataIngresos, totalDescuentosBIO = 0) {
  const base = calcularBaseAfp0608(dataIngresos, totalDescuentosBIO);
  return Math.round(base * (EXTRA_APORTE_AFP_REGIMEN_MINERO / 100) * 100) / 100;
}

/** Aporte trabajador régimen minero (PLAME 0616 - FCJMUS): 0.5% sobre base ONP o AFP. */
function valorDescuentoMinero0616(base) {
  const baseNum = Number(base) || 0;
  return Math.round(baseNum * (PORCENTAJE_APORTE_MINERO_0616 / 100) * 100) / 100;
}

function pushDescuentoMinero0616(dataDescuentos, datosAPIs, base, extras = {}) {
  const valor = valorDescuentoMinero0616(base);
  const concepto0616 = buscarConceptoPorCodigoPlame(datosAPIs, '0616');
  console.log(`⛏️ Régimen minero 0616: base=${base}, 0.5%=${valor}`);
  if (concepto0616) {
    dataDescuentos.push({
      id_concepto: concepto0616,
      ...(extras.base_calculo !== undefined ? { base_calculo: extras.base_calculo } : {}),
      valor: valor.toFixed(2),
    });
  } else {
    console.warn('⚠️ Concepto PLAME 0616 no encontrado en datosAPIs');
  }
}

/** Aporte empleador régimen minero (PLAME 0809): 2% sobre base 0608. */
function valorAporteMinero0809(dataIngresos, totalDescuentosBIO = 0) {
  const base = calcularBaseAfp0608(dataIngresos, totalDescuentosBIO);
  return Math.round(base * (EXTRA_APORTE_AFP_REGIMEN_MINERO / 100) * 100) / 100;
}

const calculosBoletaService = {
  /**
   * Función principal - recibe todos los datos organizados con información del origen
   * @param {Object} datosCompletos - Objeto con todos los datos
   * @param {Object} datosCompletos.datosEmpresa - Información de la empresa
   * @param {Object} datosCompletos.datosAPIs - Regímenes, PLAME, fórmulas, atributos
   * @param {Object} datosCompletos.datosSalariales - Sueldo, sistema pensionario, AFP
   * @param {Object} datosCompletos.conceptosUsuario - Conceptos agregados manualmente
   * @param {Object} datosCompletos.datosEmpleado - Información del empleado
   * @param {string} origen - Origen de la llamada al servicio
   */
  async generarCalculos(datosCompletos, origen = 'formulario_empleado') {
    try {
      console.log('🧮 Servicio de cálculos iniciado');
      console.log('📍 Origen:', origen);
      console.log('📊 Datos recibidos:', datosCompletos);

      // Aquí implementas tu lógica compleja
      // Tienes acceso a todos los arrays y datos:
      
      const {
        datosEmpresa,      // Empresa seleccionada
        datosAPIs,         // regimenesLaborales, conceptosPlame, formulas, atributos
        datosSalariales,   // sueldoBasico, sistemaPensionario, afp, etc.
        conceptosUsuario,  // ingresos, descuentos, aportes agregados
        datosEmpleado      // nombres, apellidos, documento, etc.
      } = datosCompletos;

      // Información del origen para contexto
      const metadatosOrigen = {
        origen: origen,
        timestamp: new Date().toISOString(),
        fuente: this.determinarFuente(origen),
        contexto: this.obtenerContexto(origen)
      };

      console.log('📍 Metadatos del origen:', metadatosOrigen);

      if (origen === 'planilla_construccion_civil') {
        const mapearLineaCierre = (item) => {
          const codigoPlame = codigoPlameParaDeclaracionRem(item) || item.codigoPlame || item.codigo;
          const hallado = resolverConceptoPlame(
            datosAPIs?.conceptosPlame || [],
            codigoPlame,
            item.concepto || item.descripcion
          );
          const base = item.base_calculo ?? item.base ?? null;
          const descripcion = item.descripcion || item.concepto || item.nombre || hallado.descripcion;
          return {
            id_concepto: {
              id: item.plame_configuracion_id || item.plame_configuracion?.id || hallado.id || null,
              codigo: hallado.codigo_plame || hallado.codigo || codigoPlame,
              codigo_plame: hallado.codigo_plame || hallado.codigo || codigoPlame,
              descripcion,
            },
            valor: Number(item.monto) || 0,
            base,
            base_calculo: base,
            formula: item.formula,
            exportable_plame: item.exportable_plame !== false,
            grupo: item.grupo || null,
          };
        };
        const ingresos = (conceptosUsuario?.ingresos || []).map(mapearLineaCierre);
        const descuentos = (conceptosUsuario?.descuentos || []).map(mapearLineaCierre);
        const aportes = (conceptosUsuario?.aportes || []).map(mapearLineaCierre);
        return {
          success: true,
          data: {
            mensaje: 'Boleta construcción civil: motor diario (sin /30)',
            origen: metadatosOrigen,
            dataIngresos: ingresos,
            dataDescuentos: descuentos,
            dataAportes: aportes,
            construccionCivil: datosCompletos.construccionCivil || null,
          },
          message: 'Servicio ejecutado exitosamente',
        };
      }

      const tipoRegimenConsu = clasificarRegimenBoleta(
        datosCompletos.datosSalariales?.regimenLaboral,
        datosCompletos.datosAPIs?.regimenesLaborales
      );
      if (tipoRegimenConsu === 'construccion_civil') {
        return await armarBoletaDiariaConstruccion(datosCompletos, metadatosOrigen);
      }

      // TODO: Implementar tu lógica compleja aquí
      // Ahora también tienes acceso al origen de la llamada
      if (origen === 'formulario_empleados_boton_generar') {
        console.log('🔄 Iniciando cálculos de boleta... con origen formulario_empleados_boton_generar');
        console.log('📍 Datos recibidos:', datosCompletos);
        // empesamos con el tipo de contratacion
        const tipoContratacion = datosCompletos.datosSalariales.tipoContratacion;
        const tipoPlanilla = datosCompletos.datosSalariales.tipoPlanilla;
        const conceptosIngresos = datosCompletos.datosAPIs.conceptosPlame[20];
        const conceptosAsignacionFamiliar = datosCompletos.datosAPIs.conceptosPlame[28];
        const conceptosBono = datosCompletos.datosAPIs.conceptosPlame[117];
        const sueldoBasico = datosCompletos.datosSalariales.sueldoBasico;
        // Dual (PLAME + interna) siempre calcula boleta PLAME completa aunque
        // la contratación residual sea "Interno" (estado previo a solo_interna).
        const aplicarCalculoBoletaCompleto =
          tipoContratacion === 'Planilla' || tipoPlanilla === 'dual';
        console.log('📍 Tipo de contratacion:', tipoContratacion, '| tipoPlanilla:', tipoPlanilla);
        // si el tipo de contratacion es planilla (o dual), entonces se aplica el calculo de boleta
        if (aplicarCalculoBoletaCompleto) {
          console.log('🔄 Se aplica el calculo de boleta');
          const regimenLaboral = datosCompletos.datosSalariales.regimenLaboral;
          console.log('📍 Regimen laboral:', regimenLaboral);
          const dataIngresos = [];
          const dataDescuentos = [];
          const dataAportes = [];
          const IgGrati = []
          const tipoRegimen = clasificarRegimenBoleta(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales);

          if (tipoRegimen === 'general') {
            console.log('🔄 Se aplica el calculo de boleta para el regimen general');
            dataIngresos.push({
                id_concepto: conceptosIngresos,
                valor: sueldoBasico
              })
              IgGrati.push({
                grati: 2,
                essalud: true
              })
              if (datosSalariales.asignacionFamiliar) {
                dataIngresos.push({
                    id_concepto: conceptosAsignacionFamiliar,
                    valor: datosAPIs.sueldoMinimo.monto * 0.1 
                  })
              }
              if (datosSalariales.tieneBono) {
                dataIngresos.push({
                    id_concepto: conceptosBono,
                    valor: datosSalariales.montoBono
                  })
              }
              console.log('datos ingresos', dataIngresos);
          } else if (tipoRegimen === 'mype_micro') {
            console.log('🔄 Se aplica el calculo de boleta para regimen microempresa');
            dataIngresos.push({
                id_concepto: conceptosIngresos,
                valor: sueldoBasico
              })
              IgGrati.push({
                grati: 1,
                essalud: true
              })
              if (datosSalariales.asignacionFamiliar) {
                dataIngresos.push({
                    id_concepto: conceptosAsignacionFamiliar,
                    valor: datosAPIs.sueldoMinimo.monto * 0.1 
                  })
              }
              console.log('datos ingresos', dataIngresos);
          } else if (tipoRegimen === 'mype_pequena') {
            console.log('🔄 Se aplica el calculo de boleta para regimen pequeña empresa');
            dataIngresos.push({
                id_concepto: conceptosIngresos,
                valor: sueldoBasico
              })
              IgGrati.push({
                grati: 0,
                essalud: false
              })
              if (datosSalariales.asignacionFamiliar) {
                dataIngresos.push({
                    id_concepto: conceptosAsignacionFamiliar,
                    valor: datosAPIs.sueldoMinimo.monto * 0.1 
                  })
              }
              console.log('datos ingresos', dataIngresos);
              
          } else if (tipoRegimen === 'agrario') {
            console.log('🔄 Régimen Agrario: ingresos 0121 + 0314 (Ley 31110), proyección gratificación tipo MYPE (grati 1)');
            const concepto0121 = datosCompletos.datosAPIs.conceptosPlame.find(
              (c) => c.codigo === '0121' || c.codigo_plame === '0121'
            );
            const concepto0314 = datosCompletos.datosAPIs.conceptosPlame.find(
              (c) => c.codigo === '0314' || c.codigo_plame === '0314'
            );
            if (concepto0121) {
              dataIngresos.push({ id_concepto: concepto0121, valor: sueldoBasico });
            } else {
              console.warn('⚠️ No se encontró concepto PLAME 0121');
            }
            const monto0314 = valorBonificacionAgraria0314(datosCompletos.datosAPIs, regimenLaboral, sueldoBasico);
            if (concepto0314 && monto0314 > 0) {
              dataIngresos.push({ id_concepto: concepto0314, valor: monto0314 });
            } else if (!concepto0314) {
              console.warn('⚠️ No se encontró concepto PLAME 0314');
            }
            IgGrati.push({ grati: 1, essalud: true });
            if (datosSalariales.asignacionFamiliar && conceptosAsignacionFamiliar) {
              dataIngresos.push({
                id_concepto: conceptosAsignacionFamiliar,
                valor: datosAPIs.sueldoMinimo.monto * 0.1,
              });
            }
            if (datosSalariales.tieneBono && conceptosBono) {
              dataIngresos.push({
                id_concepto: conceptosBono,
                valor: datosSalariales.montoBono,
              });
            }
            console.log('datos ingresos (agrario)', dataIngresos);
          } else if (tipoRegimen === 'formacion') {
            console.log('🔄 Régimen de Formación Laboral: solo código 0121 (Remuneración Básica)');
            // Buscar el concepto 0121 (Remuneración o Jornal Básico)
            const concepto0121 = datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0121" || concepto.codigo_plame === "0121");
            if (concepto0121) {
              dataIngresos.push({
                id_concepto: concepto0121,
                valor: sueldoBasico
              });
            } else {
              console.warn('⚠️ No se encontró el concepto 0121 en conceptosPlame');
            }
            // No se agregan descuentos ni aportes para este régimen
            console.log('datos ingresos (solo 0121):', dataIngresos);
            
            // Retornar directamente sin calcular descuentos ni aportes
            return {
              success: true,
              data: {
                mensaje: 'Régimen de Formación Laboral: solo ingreso básico (0121)',
                origen: metadatosOrigen,
                dataIngresos: dataIngresos,
                dataDescuentos: [],
                dataAportes: []
              },
              message: 'Servicio ejecutado exitosamente'
            };
          }else{
            return {
                success: false,
                error: 'No se puede calcular la boleta para este regimen laboral',
                data: null
              };
          }

          const idRegimenLaboral = datosCompletos.datosAPIs.regimenesLaborales.find(regimen => regimen.nombre === regimenLaboral);
          // calculo descuentos
          const tipoAporte = datosCompletos.datosSalariales.sistemaPensionario;

          let baseCalculo = 0
          

          // calculo descuentos
          if (tipoAporte ==='AFP'){
            console.log('tipoAporte', tipoAporte);
            let porA = 0
            // primero el tipo de comision el codigo es 0601
            console.log('datosCompletos.datosSalariales.tipoAfp', datosCompletos.datosSalariales.tipoAfp);
            if (datosCompletos.datosSalariales.tipoAfp === 'Flujo'){
              const ComixAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == datosCompletos.datosSalariales.afp);
              console.log('ComixAFP', ComixAFP);
              porA = ComixAFP.comision_fija;
              console.log('porA', porA);
            }else{
              const ComixAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == datosCompletos.datosSalariales.afp);
              console.log('ComixAFP', ComixAFP);
              porA = ComixAFP.comision_variable;
              console.log('porA', porA);
            }

            const formulaAFPcomision = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0601',
              'R*A'
            );
            console.log('formula para comision AFP', formulaAFPcomision);
            
            dataIngresos.forEach(
              ingreso => {
                  const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                  if(afectaSistemaPrivado){
                      baseCalculo += ingreso.valor;
                  }
              }
            );
            const formulaReemplazada = formulaAFPcomision.replace('R', baseCalculo);
            console.log('formulaReemplazada', formulaReemplazada);
            const remmplazarA = formulaReemplazada.replace('A', porA/100);
            console.log('remmplazarA', remmplazarA);
            const descuento = eval(remmplazarA);
            console.log('descuento', descuento);
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0601"),
                base_calculo: baseCalculo,
                valor: descuento.toFixed(2)
            })

            // ahora el decuento para el codigo 0606 (Seguro AFP) - Cero si el empleado tiene 65 años o más
            baseCalculo = 0;

            const fechaNacimiento = datosCompletos.datosEmpleado?.fechaNacimiento;
            let edadEmpleado = null;
            if (fechaNacimiento) {
              const nacimiento = new Date(fechaNacimiento);
              const hoy = new Date();
              edadEmpleado = hoy.getFullYear() - nacimiento.getFullYear();
              const cumpleEsteAnio = new Date(hoy.getFullYear(), nacimiento.getMonth(), nacimiento.getDate());
              if (hoy < cumpleEsteAnio) edadEmpleado -= 1;
              console.log('📅 Edad empleado (seguro AFP):', edadEmpleado, 'años');
            }

            let Segdescuento = 0;
            if (edadEmpleado !== null && edadEmpleado >= 65) {
              console.log('✅ Empleado con 65 años o más: seguro AFP (0606) = 0');
            } else {
              const SegAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre ==datosCompletos.datosSalariales.afp);
              console.log('SegAFP', SegAFP);
              const SegA = SegAFP.seguro;
              console.log('SegA', SegA);

              const formulaSegAFP = obtenerFormulaOMenos(
                datosCompletos.datosAPIs.formulas,
                datosCompletos.datosAPIs.regimenesLaborales,
                idRegimenLaboral,
                '0606',
                'R*A'
              );
              console.log('formula para seguro AFP', formulaSegAFP);
              console.log('formulaAFPcomision', formulaAFPcomision);

              dataIngresos.forEach(
                ingreso => {
                    const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                    if(afectaSistemaPrivado){
                        baseCalculo += ingreso.valor;
                    }
                }
              );

              const formulaSegReemplazada = formulaSegAFP.replace('R', baseCalculo);
              console.log('formulaSegReemplazada', formulaSegReemplazada);
              const SegReemplazada = formulaSegReemplazada.replace('A', SegA/100);
              console.log('SegReemplazada', SegReemplazada);
              Segdescuento = eval(SegReemplazada);
              console.log('Segdescuento', Segdescuento);
            }

            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0606"),
                base_calculo: baseCalculo,
                valor: Segdescuento.toFixed(2)
            })

            // ahora el decuento para el codigo 0608
            baseCalculo = 0;

            const AporAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == datosCompletos.datosSalariales.afp);
            console.log('AporAFP', AporAFP);
            const AporA = Number(AporAFP.aporte) || 0;
            console.log('AporA', AporA);

            const formulaAporAFP = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0608',
              'R*A'
            );
            console.log('formula para aporte AFP', formulaAporAFP);
            
            dataIngresos.forEach(
              ingreso => {
                  const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                  if(afectaSistemaPrivado){
                      baseCalculo += ingreso.valor;
                  }
              }
            );

            const formulaAporReemplazada = formulaAporAFP.replace('R', baseCalculo);
            console.log('formulaAporReemplazada', formulaAporReemplazada);
            const AporReemplazada = formulaAporReemplazada.replace('A', AporA/100);
            console.log('AporReemplazada', AporReemplazada);
            const Apordescuento = eval(AporReemplazada);
            console.log('Apordescuento', Apordescuento);
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0608"),
                base_calculo: baseCalculo,
                valor: Apordescuento.toFixed(2)
            })

            if (esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)) {
              const baseAfp0608 = calcularBaseAfp0608(dataIngresos);
              const descuentoMinero0611 = valorDescuentoMinero0611(dataIngresos);
              const concepto0611 = buscarConceptoPorCodigoPlame(datosCompletos.datosAPIs, '0611');
              console.log(`⛏️ Régimen minero 0611: base AFP=${baseAfp0608}, 2%=${descuentoMinero0611}`);
              if (concepto0611) {
                dataDescuentos.push({
                  id_concepto: concepto0611,
                  base_calculo: baseAfp0608,
                  valor: descuentoMinero0611.toFixed(2),
                });
              } else {
                console.warn('⚠️ Concepto PLAME 0611 no encontrado en datosAPIs');
              }
              pushDescuentoMinero0616(
                dataDescuentos,
                datosCompletos.datosAPIs,
                baseAfp0608,
                { base_calculo: baseAfp0608 }
              );
            }

           console.log('datos descuentos', dataDescuentos);

          }else{//ONP

            // busqueda de id en regimen laboral  
            console.log('idRegimenLaboral', idRegimenLaboral.id);

            const formulaONP = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0607',
              'R*13%'
            );
            console.log('formula PARA ONP', formulaONP);

            // calculo base de calculo para descuento ONP
            dataIngresos.forEach(
                ingreso => {
                    const afectaSistemaNacional = ingreso.id_concepto?.afectaciones?.sistema_nacional_19990 || ingreso.id_concepto?.sistema_nacional_19990;
                    if(afectaSistemaNacional){
                        baseCalculo += ingreso.valor * 1;
                    }
                }
            );

            let exprONP = String(formulaONP).replace(/\bR\b/g, String(baseCalculo));
            exprONP = exprONP.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
            console.log('formula ONP evaluada como', exprONP);
            const descuento = eval(exprONP);
            
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0607"),
                valor: descuento.toFixed(2)
            })

            if (esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)) {
              pushDescuentoMinero0616(dataDescuentos, datosCompletos.datosAPIs, baseCalculo);
            }

            console.log('datos descuentos', dataDescuentos);
          }
          // seguimos con descuentos ahora haremos renta

          // 💰 VALIDAR SI LA CONFIGURACIÓN DE RENTA DE QUINTA ESTÁ ACTIVADA
          const rentaQuintaActivada = datosCompletos.datosAPIs?.rentaQuintaActivada || datosCompletos.datosAPIs?.configuracionRentaQuinta?.activo || false;
          console.log('💸 Configuración de Renta de Quinta:', datosCompletos.datosAPIs?.configuracionRentaQuinta);
          console.log('🔄 Renta de Quinta Activada:', rentaQuintaActivada);

          if (rentaQuintaActivada) {
            console.log('✅ Renta de quinta activada, procediendo con el cálculo...');
            
            baseCalculo = 0;
            // primero calculamos los ingresos afectos a renta
            dataIngresos.forEach(
                ingreso => {
                    if((ingreso.id_concepto?.afectaciones?.renta_5ta_categoria) || (ingreso.id_concepto?.renta_5ta_categoria)){
                        baseCalculo += ingreso.valor * 1;
                    }
                }
            );

            console.log('baseCalculo Renta', baseCalculo);
            console.log('grati', IgGrati[0].grati);
            console.log('essalud', IgGrati[0].essalud);

            // empesamos con proyecciones de renta
            const proyeccionIngreso  = baseCalculo * 12
            const proyeccionGrati = baseCalculo * IgGrati[0].grati
            const proyeccionEssalud = IgGrati[0].essalud ? (baseCalculo * 0.09) * IgGrati[0].grati : 0

            console.log('proyeccionIngreso', proyeccionIngreso);
            console.log('proyeccionGrati', proyeccionGrati);
            console.log('proyeccionEssalud', proyeccionEssalud);

            // ahora calculamos la renta
            const SumatorioRenta = proyeccionIngreso + proyeccionGrati + proyeccionEssalud
            console.log('SumatorioRenta', SumatorioRenta);

            
            if(SumatorioRenta > datosCompletos.datosAPIs.uit.monto * 7){
              console.log('se aplica renta');
              const BIrenta = SumatorioRenta - (datosCompletos.datosAPIs.uit.monto * 7)
              console.log('renta', BIrenta);

              // empesamos con los tramos de renta
              const tramo1 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 1)
              if (BIrenta > tramo1.hasta * datosCompletos.datosAPIs.uit.monto){
                  const tramo2 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 2)
                  if (BIrenta > tramo2.hasta * datosCompletos.datosAPIs.uit.monto){
                      const tramo3 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 3)
                      if (BIrenta > tramo3.hasta * datosCompletos.datosAPIs.uit.monto){
                          const tramo4 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 4)
                          if (BIrenta > tramo4.hasta * datosCompletos.datosAPIs.uit.monto){
                              const tramo5 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 5)
                              if (BIrenta > tramo5.hasta * datosCompletos.datosAPIs.uit.monto){
                                const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                                const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                                const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                                const RetencionTramo4 = ((BIrenta - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo4.tasa/100))
                                const RetencionTramo5 = ((BIrenta - (tramo4.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo5.tasa/100))
                                const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3 + RetencionTramo4 + RetencionTramo5)/12
                                console.log('RetencionRenta', RetencionRenta);
                                dataDescuentos.push({
                                  id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605"),
                                  valor: RetencionRenta.toFixed(2)
                              })
                              }
                          }else{
                            const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                            const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                            const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                            const RetencionTramo4 = ((BIrenta - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo4.tasa/100))
                            const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3 + RetencionTramo4)/12
                            console.log('RetencionRenta', RetencionRenta);
                            dataDescuentos.push({
                              id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605"),
                              valor: RetencionRenta.toFixed(2)
                          })
                          }
                      }else{
                        const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                        const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                        const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                        const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3)/12
                        console.log('RetencionRenta', RetencionRenta);
                        dataDescuentos.push({
                          id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605"),
                          valor: RetencionRenta.toFixed(2)
                      })
                      }
                  }else{
                    const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                    const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                    const RetencionRenta = (RetencionTramo1 + RetencionTramo2)/12
                    console.log('RetencionRenta', RetencionRenta);
                    dataDescuentos.push({
                      id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605"),
                      valor: RetencionRenta.toFixed(2) 
                  })
                  }
              }else{
                const RetencionRenta = (BIrenta * (tramo1.tasa/100))/12
                console.log('RetencionRenta', RetencionRenta);
                dataDescuentos.push({
                  id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605"),
                  valor: RetencionRenta.toFixed(2)
              })
              }
            }else{
              console.log('no se aplica renta');
            }
          } else {
            console.log('❌ Renta de quinta desactivada en la configuración, saltando cálculo...');
          }

          // ahora calculamos los aportes
          // busqueda de id en regimen laboral  
          console.log('idRegimenLaboral', idRegimenLaboral.id);

          const formulaEssalud = obtenerFormulaOMenos(
            datosCompletos.datosAPIs.formulas,
            datosCompletos.datosAPIs.regimenesLaborales,
            idRegimenLaboral,
            '0804',
            'R*9%'
          );
          console.log('formula para Essalud', formulaEssalud);

          // Base EsSalud solo desde ingresos afectos (no arrastrar base ONP/renta)
          baseCalculo = 0;
          dataIngresos.forEach((ingreso) => {
            if (obtenerAfectacionPlame(ingreso.id_concepto, 'essalud_regular')) {
              baseCalculo += ingreso.valor;
            }
          });

          // comprar con el sueldo minimo
          if(baseCalculo < datosCompletos.datosAPIs.sueldoMinimo.monto){
            baseCalculo = datosCompletos.datosAPIs.sueldoMinimo.monto;
          }

          let formulaReemplazadaEss = String(formulaEssalud).replace(/\bR\b/g, String(baseCalculo));
          formulaReemplazadaEss = formulaReemplazadaEss.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
          console.log('formulaReemplazadaEss', formulaReemplazadaEss);
          const descuentoEssalud = eval(formulaReemplazadaEss);
          console.log('descuentoEssalud', descuentoEssalud);

          dataAportes.push({
              id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0804"),
              valor: descuentoEssalud.toFixed(2)
          })

          if (
            tipoAporte === 'AFP' &&
            esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)
          ) {
            const baseAfp0608 = calcularBaseAfp0608(dataIngresos);
            const aporteMinero0809 = valorAporteMinero0809(dataIngresos);
            const concepto0809 = buscarConceptoPorCodigoPlame(datosCompletos.datosAPIs, '0809');
            console.log(`⛏️ Aporte minero 0809: base 0608=${baseAfp0608}, 2%=${aporteMinero0809}`);
            if (concepto0809) {
              dataAportes.push({
                id_concepto: concepto0809,
                base_calculo: baseAfp0608,
                valor: aporteMinero0809.toFixed(2),
              });
            } else {
              console.warn('⚠️ Concepto PLAME 0809 no encontrado en datosAPIs');
            }
          }

          
          console.log('datos aportes', dataAportes);

          

          return {
            success: true,
            data: {
              // Aquí devuelves los resultados de tus cálculos
              mensaje: 'Datos recibidos correctamente - implementa tu lógica aquí',
              origen: metadatosOrigen,
              dataIngresos: dataIngresos,
              dataDescuentos: dataDescuentos,
              dataAportes: dataAportes
            },
            message: 'Servicio ejecutado exitosamente'
          };

        }else{
          console.log('🔄 No se aplica el calculo de boleta, solo aplica ingresos sin descuentos');
          // calculamos el ingreso basico
          console.log('concepto ingresos', conceptosIngresos);
          console.log('sueldo basico', sueldoBasico);
          const regimenLaboral = datosCompletos.datosSalariales.regimenLaboral;
          console.log('📍 Regimen laboral:', regimenLaboral);
          const dataIngresos = {
            id_concepto: conceptosIngresos,
            valor: sueldoBasico
          }
          console.log('datos ingresos', dataIngresos);
          return {
            success: true,
            data: {
              // Aquí devuelves los resultados de tus cálculos
              mensaje: 'Datos recibidos correctamente - implementa tu lógica aquí',
              origen: metadatosOrigen,
              dataIngresos: dataIngresos
            },
            message: 'Servicio ejecutado exitosamente'
          };
        }
      }else{
        console.log('🔄 Iniciando cálculos de edición de boleta...');
        console.log('📍 Datos recibidos:', datosCompletos);
        const regimenLaboral = datosCompletos.datosSalariales.regimenLaboral;

        console.log('📍 Regimen laboral:', regimenLaboral);
        // empesamos con el tipo de contratacion
        const tipoContratacion = datosCompletos.datosSalariales.tipoContratacion;
        const esReciboHonorarios =
          tipoContratacion === 'Recibo por Honorarios' ||
          String(tipoContratacion || '').toLowerCase().includes('honorario');

        // RH: ingresos + descuentos operativos (faltas, tardanzas, etc.).
        // Sin AFP/ONP/renta ni aportes EsSalud (no se recalculan conceptos de planilla).
        if (esReciboHonorarios) {
          console.log('📋 Recibo por Honorarios: ingresos y descuentos operativos (sin pensionarios ni aportes)');
          const codigosDescuentoPlanillaRh = ['0601', '0605', '0606', '0607', '0608', '0611', '0616'];
          const dataIngresosRh = [];
          (datosCompletos.conceptosUsuario?.ingresos || []).forEach((ingreso) => {
            const dataIngreso = datosCompletos.datosAPIs.conceptosIngresos?.find(
              (concepto) =>
                concepto.codigo === ingreso.codigoPlame ||
                concepto.codigo_plame === ingreso.codigoPlame
            ) || datosCompletos.datosAPIs.conceptosPlame?.find(
              (concepto) =>
                concepto.codigo === ingreso.codigoPlame ||
                concepto.codigo_plame === ingreso.codigoPlame
            );
            if (dataIngreso || ingreso.monto != null) {
              dataIngresosRh.push({
                id_concepto: dataIngreso || {
                  codigo_plame: ingreso.codigoPlame,
                  codigo: ingreso.codigoPlame,
                  descripcion: ingreso.concepto || ingreso.descripcion,
                },
                valor: ingreso.monto,
              });
            }
          });
          const dataDescuentosRh = [];
          (datosCompletos.conceptosUsuario?.descuentos || []).forEach((descuento) => {
            const codigo = String(descuento.codigoPlame || descuento.codigo || '');
            if (codigosDescuentoPlanillaRh.includes(codigo)) return;
            const monto = parseFloat(descuento.monto);
            if (Number.isNaN(monto) || monto <= 0) return;
            const dataDescuento = datosCompletos.datosAPIs.conceptosDescuentos?.find(
              (concepto) =>
                concepto.codigo === codigo || concepto.codigo_plame === codigo
            ) || datosCompletos.datosAPIs.conceptosPlame?.find(
              (concepto) =>
                concepto.codigo === codigo || concepto.codigo_plame === codigo
            );
            dataDescuentosRh.push({
              id_concepto: dataDescuento || {
                codigo_plame: codigo,
                codigo,
                descripcion: descuento.concepto || descuento.descripcion,
              },
              valor: monto,
            });
          });
          return {
            success: true,
            data: {
              mensaje: 'Recibo por Honorarios: ingresos y descuentos operativos',
              origen: metadatosOrigen,
              dataIngresos: dataIngresosRh,
              dataDescuentos: dataDescuentosRh,
              dataAportes: [],
            },
            message: 'Servicio ejecutado exitosamente',
          };
        }

        const tipoPlanillaEdicion = datosCompletos.datosSalariales?.tipoPlanilla;
        const aplicarCalculoBoletaCompletoEdicion =
          tipoContratacion === 'Planilla' || tipoPlanillaEdicion === 'dual';

        if (aplicarCalculoBoletaCompletoEdicion) {
          console.log('🔄 Se aplica el calculo de boleta', {
            tipoContratacion,
            tipoPlanilla: tipoPlanillaEdicion,
          });
          const dataIngresos = [];
          const dataDescuentos = [];
          const dataAportes = [];

          const IgGrati = [];

          ingresosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(ingreso => {
              const dataIngreso = datosCompletos.datosAPIs.conceptosIngresos.find(concepto => concepto.codigo === ingreso.codigoPlame || concepto.codigo_plame === ingreso.codigoPlame);
              dataIngresos.push({
                id_concepto: dataIngreso || {
                  codigo_plame: ingreso.codigoPlame,
                  codigo: ingreso.codigoPlame,
                  descripcion: ingreso.concepto || ingreso.descripcion,
                },
                valor: ingreso.monto,
                excluir_boleta_ordinaria: Boolean(ingreso.excluirBoletaOrdinaria || ingreso.excluir_boleta_ordinaria),
                origen_calculo: ingreso.origenCalculo || ingreso.origen_calculo || null,
              });
            });
          console.log('dataIngresos', dataIngresos);

          const tipoRegimenEdicion = clasificarRegimenBoleta(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales);

          if (tipoRegimenEdicion === 'general') {
            console.log('🔄 Se aplica el calculo de boleta para el regimen general');
              IgGrati.push({
                grati: 2,
                essalud: true
              })
              console.log('datos ingresos', dataIngresos);
          } else if (tipoRegimenEdicion === 'mype_micro') {
            console.log('🔄 Se aplica el calculo de boleta para regimen microempresa');
              IgGrati.push({
                grati: 1,
                essalud: true
              })
              console.log('datos ingresos', dataIngresos);
          } else if (tipoRegimenEdicion === 'mype_pequena') {
            console.log('🔄 Se aplica el calculo de boleta para regimen pequeña empresa');
              IgGrati.push({
                grati: 0,
                essalud: false
              })
              console.log('datos ingresos', dataIngresos);
              
          } else if (tipoRegimenEdicion === 'agrario') {
            console.log('🔄 Edición: Régimen Agrario — gratificación proyectada ×1 (medio sueldo), 0314 si falta');
            IgGrati.push({ grati: 1, essalud: true });
            const tiene0314 = dataIngresos.some(
              (i) => i.id_concepto && (i.id_concepto.codigo === '0314' || i.id_concepto.codigo_plame === '0314')
            );
            const fila0121 = dataIngresos.find(
              (i) => i.id_concepto && (i.id_concepto.codigo === '0121' || i.id_concepto.codigo_plame === '0121')
            );
            const baseR = fila0121
              ? Number(fila0121.valor)
              : parseFloat(datosCompletos.datosSalariales.sueldoBasico) || 0;
            if (!tiene0314) {
              const concepto0314 = datosCompletos.datosAPIs.conceptosPlame.find(
                (c) => c.codigo === '0314' || c.codigo_plame === '0314'
              );
              const monto0314 = valorBonificacionAgraria0314(
                datosCompletos.datosAPIs,
                regimenLaboral,
                baseR
              );
              if (concepto0314 && monto0314 > 0) {
                dataIngresos.push({ id_concepto: concepto0314, valor: monto0314 });
              }
            }
            console.log('datos ingresos (agrario edición)', dataIngresos);
          } else if (tipoRegimenEdicion === 'formacion') {
            console.log('🔄 Régimen de Formación Laboral (edición): solo código 0121; descuentos y aportes (ONP, AFP, EsSalud, Renta) se pasan del usuario');
            // Filtrar solo el concepto 0121 de los ingresos del usuario
            const ingresosFormacion = [];
            ingresosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(ingreso => {
              if (ingreso.codigoPlame === "0121") {
                const dataIngreso = datosCompletos.datosAPIs.conceptosIngresos.find(concepto => concepto.codigo === "0121" || concepto.codigo_plame === "0121");
                if (dataIngreso) {
                  ingresosFormacion.push({
                    id_concepto: dataIngreso,
                    valor: ingreso.monto
                  });
                }
              }
            });
            console.log('datos ingresos (solo 0121):', ingresosFormacion);

            // Pasar descuentos del usuario (no se calculan ONP, AFP, EsSalud, Renta en este régimen)
            const dataDescuentosFormacion = [];
            if (datosCompletos.conceptosUsuario.descuentos && Array.isArray(datosCompletos.conceptosUsuario.descuentos)) {
              descuentosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(descuento => {
                const dataDescuento = datosCompletos.datosAPIs.conceptosDescuentos?.find(concepto => concepto.codigo === descuento.codigoPlame || concepto.codigo_plame === descuento.codigoPlame);
                if (dataDescuento) {
                  dataDescuentosFormacion.push({
                    id_concepto: dataDescuento,
                    valor: descuento.monto
                  });
                }
              });
            }
            // Pasar aportes del usuario (no se calcula EsSalud 0804 en este régimen)
            const dataAportesFormacion = [];
            if (datosCompletos.conceptosUsuario.aportes && Array.isArray(datosCompletos.conceptosUsuario.aportes)) {
              datosCompletos.conceptosUsuario.aportes.forEach(aporte => {
                agregarAporteUsuario(dataAportesFormacion, datosCompletos.datosAPIs, aporte);
              });
            }
            console.log('datos descuentos (pasados del usuario):', dataDescuentosFormacion);
            console.log('datos aportes (pasados del usuario):', dataAportesFormacion);

            return {
              success: true,
              data: {
                mensaje: 'Régimen de Formación Laboral: solo ingreso básico (0121); descuentos y aportes del usuario',
                origen: metadatosOrigen,
                dataIngresos: ingresosFormacion,
                dataDescuentos: dataDescuentosFormacion,
                dataAportes: dataAportesFormacion
              },
              message: 'Servicio ejecutado exitosamente'
            };
          } else if (origen === 'planilla_calculo_service') {
            // Planilla: permitir cualquier régimen (ej. régimen RH); conceptos ya vienen calculados desde tareos/planilla
            console.log('🔄 Origen planilla_calculo_service: se acepta régimen no estándar (ej. RH), usando conceptos ya calculados');
            const dataDescuentosPassthrough = [];
            if (datosCompletos.conceptosUsuario.descuentos && Array.isArray(datosCompletos.conceptosUsuario.descuentos)) {
              descuentosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(descuento => {
                const dataDescuento = datosCompletos.datosAPIs.conceptosDescuentos?.find(concepto => concepto.codigo === descuento.codigoPlame || concepto.codigo_plame === descuento.codigoPlame);
                if (dataDescuento) {
                  dataDescuentosPassthrough.push({ id_concepto: dataDescuento, valor: descuento.monto });
                }
              });
            }
            const dataAportesPassthrough = [];
            if (datosCompletos.conceptosUsuario.aportes && Array.isArray(datosCompletos.conceptosUsuario.aportes)) {
              datosCompletos.conceptosUsuario.aportes.forEach(aporte => {
                agregarAporteUsuario(dataAportesPassthrough, datosCompletos.datosAPIs, aporte);
              });
            }
            return {
              success: true,
              data: {
                mensaje: 'Boleta desde planilla (régimen no estándar): conceptos calculados por tareos',
                origen: metadatosOrigen,
                dataIngresos: dataIngresos,
                dataDescuentos: dataDescuentosPassthrough,
                dataAportes: dataAportesPassthrough
              },
              message: 'Servicio ejecutado exitosamente'
            };
          }else{
            return {
                success: false,
                error: 'No se puede calcular la boleta para este regimen laboral',
                data: null
              };
          }

          // ahora calculamos los descuentos

          const idRegimenLaboral = datosCompletos.datosAPIs.regimenesLaborales.find(regimen => regimen.nombre === regimenLaboral);
          // calculo descuentos
          const tipoAporte = datosCompletos.datosSalariales.sistemaPensionario;

          let baseCalculo = 0;
          

          // calculo descuentos
          if (tipoAporte ==='AFP'){
            console.log('tipoAporte', tipoAporte);

            // Misma lógica que ONP: restar inasistencias/tardanzas (0704, 0705, 0707) de la base AFP
            const totalDescuentosBIOAFP = obtenerTotalDescuentosBIO(
              datosCompletos.conceptosUsuario?.descuentos || []
            );
            console.log(
              '💰 Total descuentos BIO a restar de base AFP (0705 + 0704 + 0707):',
              totalDescuentosBIOAFP
            );

            let porA = 0
            console.log('datosCompletos.datosSalariales.tipoAfp', datosCompletos.datosSalariales.tipoAfp);
            // primero el tipo de comision el codigo es 0601
            if (datosCompletos.datosSalariales.tipoAfp === 'Flujo'){
              const ComixAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == "AFP " + datosCompletos.datosSalariales.afp || afp.nombre == datosCompletos.datosSalariales.afp);
              console.log('ComixAFP', ComixAFP);
              porA = ComixAFP.comision_fija;
              console.log('porA', porA);
            }else{
              const ComixAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == "AFP " + datosCompletos.datosSalariales.afp || afp.nombre == datosCompletos.datosSalariales.afp);
              console.log('ComixAFP', ComixAFP);
              porA = ComixAFP.comision_variable;
              console.log('porA', porA);
            }

            const formulaAFPcomision = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0601',
              'R*A'
            );
            console.log('formula para comision AFP', formulaAFPcomision);
            
            dataIngresos.forEach(
              ingreso => {
                  const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                  if(afectaSistemaPrivado){
                      baseCalculo += ingreso.valor;
                  }
              }
            );
            baseCalculo = Math.max(0, baseCalculo - totalDescuentosBIOAFP);
            console.log('baseCalculo AFP 0601 (tras BIO):', baseCalculo);
            const formulaReemplazada = formulaAFPcomision.replace('R', baseCalculo);
            console.log('formulaReemplazada', formulaReemplazada);
            const remmplazarA = formulaReemplazada.replace('A', porA/100);
            console.log('remmplazarA', remmplazarA);
            const descuento = eval(remmplazarA);
            console.log('descuento', descuento);
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0601" || concepto.codigo_plame === "0601"),
                base_calculo: baseCalculo,
                valor: descuento.toFixed(2)
            })

            // ahora el decuento para el codigo 0606 (Seguro AFP) - Cero si el empleado tiene 65 años o más
            baseCalculo = 0;

            const fechaNacimientoEd = datosCompletos.datosEmpleado?.fechaNacimiento;
            let edadEmpleadoEd = null;
            if (fechaNacimientoEd) {
              const nacimientoEd = new Date(fechaNacimientoEd);
              const hoyEd = new Date();
              edadEmpleadoEd = hoyEd.getFullYear() - nacimientoEd.getFullYear();
              const cumpleEsteAnioEd = new Date(hoyEd.getFullYear(), nacimientoEd.getMonth(), nacimientoEd.getDate());
              if (hoyEd < cumpleEsteAnioEd) edadEmpleadoEd -= 1;
              console.log('📅 Edad empleado (seguro AFP - edición):', edadEmpleadoEd, 'años');
            }

            let SegdescuentoEd = 0;
            if (edadEmpleadoEd !== null && edadEmpleadoEd >= 65) {
              console.log('✅ Empleado con 65 años o más: seguro AFP (0606) = 0');
            } else {
              const SegAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == "AFP " + datosCompletos.datosSalariales.afp || afp.nombre == datosCompletos.datosSalariales.afp);
              console.log('SegAFP', SegAFP);
              const SegA = SegAFP.seguro;
              console.log('SegA', SegA);

              const formulaSegAFP = obtenerFormulaOMenos(
                datosCompletos.datosAPIs.formulas,
                datosCompletos.datosAPIs.regimenesLaborales,
                idRegimenLaboral,
                '0606',
                'R*A'
              );
              console.log('formula para seguro AFP', formulaSegAFP);
              console.log('formulaAFPcomision', formulaAFPcomision);

              dataIngresos.forEach(
                ingreso => {
                    const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                    if(afectaSistemaPrivado){
                        baseCalculo += ingreso.valor;
                    }
                }
              );
              baseCalculo = Math.max(0, baseCalculo - totalDescuentosBIOAFP);
              console.log('baseCalculo AFP 0606 (tras BIO):', baseCalculo);

              const formulaSegReemplazada = formulaSegAFP.replace('R', baseCalculo);
              console.log('formulaSegReemplazada', formulaSegReemplazada);
              const SegReemplazada = formulaSegReemplazada.replace('A', SegA/100);
              console.log('SegReemplazada', SegReemplazada);
              SegdescuentoEd = eval(SegReemplazada);
              console.log('Segdescuento', SegdescuentoEd);
            }

            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0606" || concepto.codigo_plame === "0606"),
                base_calculo: baseCalculo,
                valor: SegdescuentoEd.toFixed(2)
            })

            // ahora el decuento para el codigo 0608
            baseCalculo = 0;

            const AporAFP = datosCompletos.datosAPIs.afps.find(afp => afp.nombre == "AFP " + datosCompletos.datosSalariales.afp || afp.nombre == datosCompletos.datosSalariales.afp);
            console.log('AporAFP', AporAFP);
            const AporA = Number(AporAFP.aporte) || 0;
            console.log('AporA', AporA);

            const formulaAporAFP = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0608',
              'R*A'
            );
            console.log('formula para aporte AFP', formulaAporAFP);
            
            dataIngresos.forEach(
              ingreso => {
                  const afectaSistemaPrivado = ingreso.id_concepto?.afectaciones?.sistema_privado_pension || ingreso.id_concepto?.sistema_privado_pension;
                  if(afectaSistemaPrivado){
                      baseCalculo += ingreso.valor;
                  }
              }
            );
            baseCalculo = Math.max(0, baseCalculo - totalDescuentosBIOAFP);
            console.log('baseCalculo AFP 0608 (tras BIO):', baseCalculo);

            const formulaAporReemplazada = formulaAporAFP.replace('R', baseCalculo);
            console.log('formulaAporReemplazada', formulaAporReemplazada);
            const AporReemplazada = formulaAporReemplazada.replace('A', AporA/100);
            console.log('AporReemplazada', AporReemplazada);
            const Apordescuento = eval(AporReemplazada);
            console.log('Apordescuento', Apordescuento);
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0608" || concepto.codigo_plame === "0608"),
                base_calculo: baseCalculo,
                valor: Apordescuento.toFixed(2)
            })

            if (esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)) {
              const baseAfp0611 = calcularBaseAfp0608(dataIngresos, totalDescuentosBIOAFP);
              const descuentoMinero0611 = valorDescuentoMinero0611(dataIngresos, totalDescuentosBIOAFP);
              const concepto0611 = buscarConceptoPorCodigoPlame(datosCompletos.datosAPIs, '0611');
              console.log(`⛏️ Régimen minero 0611 (edición): base AFP=${baseAfp0611}, 2%=${descuentoMinero0611}`);
              if (concepto0611) {
                dataDescuentos.push({
                  id_concepto: concepto0611,
                  base_calculo: baseAfp0611,
                  valor: descuentoMinero0611.toFixed(2),
                });
              } else {
                console.warn('⚠️ Concepto PLAME 0611 no encontrado en datosAPIs');
              }
              pushDescuentoMinero0616(
                dataDescuentos,
                datosCompletos.datosAPIs,
                baseAfp0611,
                { base_calculo: baseAfp0611 }
              );
            }

           console.log('datos descuentos', dataDescuentos);

          }else{//ONP

            // busqueda de id en regimen laboral  
            console.log('idRegimenLaboral', idRegimenLaboral.id);

            const formulaONP = obtenerFormulaOMenos(
              datosCompletos.datosAPIs.formulas,
              datosCompletos.datosAPIs.regimenesLaborales,
              idRegimenLaboral,
              '0607',
              'R*13%'
            );
            console.log('formula PARA ONP', formulaONP);

            // calculo base de calculo para descuento ONP
            // Descuentos afectos: tardanzas e inasistencias (0704, 0705, 0707)
            const totalDescuentosBIONP = obtenerTotalDescuentosBIO(
              datosCompletos.conceptosUsuario?.descuentos || []
            );
            
            console.log('💰 Total de descuentos BIO/ONP (0705 + 0704 + 0707):', totalDescuentosBIONP);

            baseCalculo   = 0;
            dataIngresos.forEach(
                ingreso => {
                    const afectaSistemaNacional = ingreso.id_concepto?.afectaciones?.sistema_nacional_19990 || ingreso.id_concepto?.sistema_nacional_19990;
                    if(afectaSistemaNacional){
                        baseCalculo += ingreso.valor * 1;
                    }
                }
            );

            baseCalculo = Math.max(0, baseCalculo - totalDescuentosBIONP);
            console.log('baseCalculo', baseCalculo);
            let exprONPEd = String(formulaONP).replace(/\bR\b/g, String(baseCalculo));
            exprONPEd = exprONPEd.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
            console.log('formula ONP (edición) evaluada como', exprONPEd);
            const descuento = eval(exprONPEd);
            
            dataDescuentos.push({
                id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0607" || concepto.codigo_plame === "0607"),
                base_calculo: baseCalculo,
                valor: descuento.toFixed(2)
            })

            if (esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)) {
              pushDescuentoMinero0616(
                dataDescuentos,
                datosCompletos.datosAPIs,
                baseCalculo,
                { base_calculo: baseCalculo }
              );
            }

            console.log('datos descuentos', dataDescuentos);
          }

          // seguimos con descuentos ahora haremos renta

          // 💰 VALIDAR SI LA CONFIGURACIÓN DE RENTA DE QUINTA ESTÁ ACTIVADA
          const rentaQuintaActivada = datosCompletos.datosAPIs?.rentaQuintaActivada || datosCompletos.datosAPIs?.configuracionRentaQuinta?.activo || false;
          console.log('💸 Configuración de Renta de Quinta (Edición):', datosCompletos.datosAPIs?.configuracionRentaQuinta);
          console.log('🔄 Renta de Quinta Activada (Edición):', rentaQuintaActivada);

          if (rentaQuintaActivada) {
            console.log('✅ Renta de quinta activada, procediendo con el cálculo (modo edición)...');
            
            baseCalculo = 0;
            // primero calculamos los ingresos afectos a renta
            console.log('dataIngresos para renta', dataIngresos);
            dataIngresos.forEach(
                ingreso => {
                    if((ingreso.id_concepto?.afectaciones?.renta_5ta_categoria) || (ingreso.id_concepto?.renta_5ta_categoria)){
                        baseCalculo += ingreso.valor * 1;
                    }
                }
            );

            console.log('baseCalculo Renta', baseCalculo);
            console.log('grati', IgGrati[0].grati);
            console.log('essalud', IgGrati[0].essalud);

            // empesamos con proyecciones de renta

            const proyeccionIngreso  = baseCalculo * 12
            const proyeccionGrati = baseCalculo * IgGrati[0].grati
            const proyeccionEssalud = IgGrati[0].essalud ? (baseCalculo * 0.09) * IgGrati[0].grati : 0

            console.log('proyeccionIngreso', proyeccionIngreso);
            console.log('proyeccionGrati', proyeccionGrati);
            console.log('proyeccionEssalud', proyeccionEssalud);

            // ahora calculamos la renta
            const SumatorioRenta = proyeccionIngreso + proyeccionGrati + proyeccionEssalud
            console.log('SumatorioRenta', SumatorioRenta);

            
            if(SumatorioRenta > datosCompletos.datosAPIs.uit.monto * 7){
              console.log('se aplica renta');
              const BIrenta = SumatorioRenta - (datosCompletos.datosAPIs.uit.monto * 7)
              console.log('renta', BIrenta);

              // empesamos con los tramos de renta
              const tramo1 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 1)
              if (BIrenta > tramo1.hasta * datosCompletos.datosAPIs.uit.monto){
                  const tramo2 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 2)
                  if (BIrenta > tramo2.hasta * datosCompletos.datosAPIs.uit.monto){
                      const tramo3 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 3)
                      if (BIrenta > tramo3.hasta * datosCompletos.datosAPIs.uit.monto){
                          const tramo4 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 4)
                          if (BIrenta > tramo4.hasta * datosCompletos.datosAPIs.uit.monto){
                              const tramo5 = datosCompletos.datosAPIs.tramosRenta.find(tramo => tramo.tramo === 5)
                              if (BIrenta > tramo5.hasta * datosCompletos.datosAPIs.uit.monto){
                                const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                                const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                                const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                                const RetencionTramo4 = ((BIrenta - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo4.tasa/100))
                                const RetencionTramo5 = ((BIrenta - (tramo4.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo5.tasa/100))
                                const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3 + RetencionTramo4 + RetencionTramo5)/12
                                console.log('RetencionRenta', RetencionRenta);
                                dataDescuentos.push({
                                  id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605" || concepto.codigo_plame === "0605"),
                                  base_calculo: baseCalculo,
                                  valor: RetencionRenta.toFixed(2)
                              })
                              }
                          }else{
                            const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                            const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                            const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                            const RetencionTramo4 = ((BIrenta - (tramo3.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo4.tasa/100))
                            const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3 + RetencionTramo4)/12
                            console.log('RetencionRenta', RetencionRenta);
                            dataDescuentos.push({
                              id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605" || concepto.codigo_plame === "0605"),
                              base_calculo: baseCalculo,
                              valor: RetencionRenta.toFixed(2)
                          })
                          }
                      }else{
                        const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                        const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                        const RetencionTramo3 = ((BIrenta - (tramo2.hasta * datosCompletos.datosAPIs.uit.monto) - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo3.tasa/100))
                        const RetencionRenta = (RetencionTramo1 + RetencionTramo2 + RetencionTramo3)/12
                        console.log('RetencionRenta', RetencionRenta);
                        dataDescuentos.push({
                          id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605" || concepto.codigo_plame === "0605"),
                          base_calculo: baseCalculo,
                          valor: RetencionRenta.toFixed(2)
                      })
                      }
                  }else{
                    const RetencionTramo1 = ((tramo1.hasta * datosCompletos.datosAPIs.uit.monto) * (tramo1.tasa/100))
                    const RetencionTramo2 = ((BIrenta - (tramo1.hasta * datosCompletos.datosAPIs.uit.monto)) * (tramo2.tasa/100))
                    const RetencionRenta = (RetencionTramo1 + RetencionTramo2)/12
                    console.log('RetencionRenta', RetencionRenta);
                    dataDescuentos.push({
                      id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605" || concepto.codigo_plame === "0605"),
                      base_calculo: baseCalculo,
                      valor: RetencionRenta.toFixed(2) 
                  })
                  }
              }else{
                const RetencionRenta = (BIrenta * (tramo1.tasa/100))/12
                console.log('RetencionRenta', RetencionRenta);
                dataDescuentos.push({
                  id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0605" || concepto.codigo_plame === "0605"),
                  base_calculo: baseCalculo,
                  valor: RetencionRenta.toFixed(2)
              })
              }
            }else{
              console.log('no se aplica renta');
            }
          } else {
            console.log('❌ Renta de quinta desactivada en la configuración, saltando cálculo (modo edición)...');
          }
          descuentosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(descuento => {
            // ✅ CORREGIDO: Solo pasa si NO tiene ninguno de estos códigos
            const codigosExcluidos = ["0607", "0601", "0606", "0608", "0611", "0616"];
            if(!codigosExcluidos.includes(descuento.codigoPlame)){
              console.log('Paso la validacion con codigo', descuento.codigoPlame);
              const dataDescuento = datosCompletos.datosAPIs.conceptosDescuentos.find(concepto => concepto.codigo === descuento.codigoPlame || concepto.codigo_plame === descuento.codigoPlame);
              dataDescuentos.push({
                id_concepto: dataDescuento || {
                  codigo_plame: descuento.codigoPlame || descuento.codigo,
                  codigo: descuento.codigoPlame || descuento.codigo,
                  descripcion: descuento.concepto || descuento.descripcion,
                },
                valor: descuento.monto,
                exportable_plame: descuento.exportable_plame,
                origen_calculo: descuento.origenCalculo || descuento.origen_calculo || null,
                absorberEn0121: Boolean(descuento.absorberEn0121),
                fusionarEn0705: Boolean(descuento.fusionarEn0705),
              });
              console.log('datos descuentos excluidos de afp y onp y renta', dataDescuentos);
          }
          });

          // ahora calculamos los aportes
          // busqueda de id en regimen laboral  
          console.log('idRegimenLaboral', idRegimenLaboral.id);

          const formulaEssalud = obtenerFormulaOMenos(
            datosCompletos.datosAPIs.formulas,
            datosCompletos.datosAPIs.regimenesLaborales,
            idRegimenLaboral,
            '0804',
            'R*9%'
          );
          console.log('formula para Essalud (edición)', formulaEssalud);

          // calculo base de calculo para aporte EsSalud

          // 
          baseCalculo = 0;
          dataIngresos.forEach((ingreso) => {
            if (obtenerAfectacionPlame(ingreso.id_concepto, 'essalud_regular')) {
              baseCalculo += ingreso.valor;
            }
          });

          console.log('baseCalculo Essalud', baseCalculo);

          // Restar descuentos BIO/ONP (0705 y 0704) de la base de cálculo para EsSalud
          let totalDescuentosBIONP = 0;
          
          descuentosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(descuento => {
            // Códigos especiales BIO/ONP (0705 y 0704) + dominical jornal
            const codigosBIONP = ["0705", "0704","0707"];
            if(codigosBIONP.includes(descuento.codigoPlame) || esDescuentoVistaQueReduceBIO(descuento)){
              totalDescuentosBIONP += parseFloat(descuento.monto || 0);
              console.log('Descuento BIO/ONP encontrado para EsSalud:', descuento.codigoPlame, 'Monto:', descuento.monto, 'Total acumulado:', totalDescuentosBIONP);
            }
          });
          
          console.log('💰 Total de descuentos BIO/ONP para EsSalud (0705 + 0704 + 0707):', totalDescuentosBIONP);
          
          baseCalculo = baseCalculo - totalDescuentosBIONP;
          console.log('baseCalculo EsSalud después de restar BIO/ONP:', baseCalculo);

          // comprar con el sueldo minimo
          if(baseCalculo < datosCompletos.datosAPIs.sueldoMinimo.monto){
            baseCalculo = datosCompletos.datosAPIs.sueldoMinimo.monto;
          }

          let formulaReemplazadaEssEd = String(formulaEssalud).replace(/\bR\b/g, String(baseCalculo));
          formulaReemplazadaEssEd = formulaReemplazadaEssEd.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
          console.log('formulaReemplazadaEss', formulaReemplazadaEssEd);
          const descuentoEssalud = eval(formulaReemplazadaEssEd);
          console.log('descuentoEssalud', descuentoEssalud);

          dataAportes.push({
              id_concepto: datosCompletos.datosAPIs.conceptosPlame.find(concepto => concepto.codigo === "0804" || concepto.codigo_plame === "0804"),
              base_calculo: baseCalculo,
              valor: descuentoEssalud.toFixed(2)
          })

          if (
            tipoAporte === 'AFP' &&
            esRegimenMinero(regimenLaboral, datosCompletos.datosAPIs.regimenesLaborales)
          ) {
            const baseAfp0608 = calcularBaseAfp0608(dataIngresos);
            const aporteMinero0809 = valorAporteMinero0809(dataIngresos);
            const concepto0809 = buscarConceptoPorCodigoPlame(datosCompletos.datosAPIs, '0809');
            console.log(`⛏️ Aporte minero 0809 (edición): base 0608=${baseAfp0608}, 2%=${aporteMinero0809}`);
            if (concepto0809) {
              dataAportes.push({
                id_concepto: concepto0809,
                base_calculo: baseAfp0608,
                valor: aporteMinero0809.toFixed(2),
              });
            } else {
              console.warn('⚠️ Concepto PLAME 0809 no encontrado en datosAPIs');
            }
          }

          console.log('datos conceptos aportes', datosCompletos.conceptosUsuario.aportes);
          (datosCompletos.conceptosUsuario.aportes || []).forEach(aporte => {
            agregarAporteUsuario(dataAportes, datosCompletos.datosAPIs, aporte, ['0804', '0809']);
          });

          return {
            success: true,
            data: {
              // Aquí devuelves los resultados de tus cálculos
              mensaje: 'Datos recibidos correctamente - implementa tu lógica aquí',
              origen: metadatosOrigen,
              dataIngresos: dataIngresos,
              dataDescuentos: dataDescuentos,
              dataAportes: dataAportes
            },
            message: 'Servicio ejecutado exitosamente'
          };
          
        }else{
            const dataIngresos = [];
            ingresosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(ingreso => {
                const dataIngreso = datosCompletos.datosAPIs.conceptosIngresos.find(concepto => concepto.codigo === ingreso.codigoPlame || concepto.codigo_plame === ingreso.codigoPlame);
                dataIngresos.push({
                  id_concepto: dataIngreso || {
                    codigo_plame: ingreso.codigoPlame,
                    codigo: ingreso.codigoPlame,
                    descripcion: ingreso.concepto || ingreso.descripcion,
                  },
                  valor: ingreso.monto,
                  excluir_boleta_ordinaria: Boolean(ingreso.excluirBoletaOrdinaria || ingreso.excluir_boleta_ordinaria),
                  origen_calculo: ingreso.origenCalculo || ingreso.origen_calculo || null,
                });
              });
            const dataDescuentos = [];
            if (datosCompletos.conceptosUsuario.descuentos && Array.isArray(datosCompletos.conceptosUsuario.descuentos)) {
              descuentosParaBoletaPlame(datosCompletos.conceptosUsuario).forEach(descuento => {
                const dataDescuento = datosCompletos.datosAPIs.conceptosDescuentos?.find(concepto => concepto.codigo === descuento.codigoPlame || concepto.codigo_plame === descuento.codigoPlame);
                if (dataDescuento) {
                  dataDescuentos.push({ id_concepto: dataDescuento, valor: descuento.monto });
                }
              });
            }
            const dataAportes = [];
            if (datosCompletos.conceptosUsuario.aportes && Array.isArray(datosCompletos.conceptosUsuario.aportes)) {
              datosCompletos.conceptosUsuario.aportes.forEach(aporte => {
                agregarAporteUsuario(dataAportes, datosCompletos.datosAPIs, aporte);
              });
            }
            console.log('dataIngresos', dataIngresos);
            console.log('dataDescuentos', dataDescuentos);
            console.log('dataAportes', dataAportes);
            return {
              success: true,
              data: {
                mensaje: 'Datos recibidos correctamente - implementa tu lógica aquí',
                origen: metadatosOrigen,
                dataIngresos: dataIngresos,
                dataDescuentos: dataDescuentos,
                dataAportes: dataAportes
              },
              message: 'Servicio ejecutado exitosamente'
            };
        }
      }
      
      
      return {
        success: true,
        data: {
          // Aquí devuelves los resultados de tus cálculos
          mensaje: 'Datos recibidos correctamente - implementa tu lógica aquí',
          origen: metadatosOrigen,
          //dataIngresos: dataIngresos
        },
        message: 'Servicio ejecutado exitosamente'
      };

    } catch (error) {
      console.error('❌ Error en servicio de cálculos:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  /**
   * Determinar la fuente basada en el origen
   */
  determinarFuente(origen) {
    const fuentes = {
      'formulario_empleados_boton_generar': 'Formulario Empleados - Botón Generar',
      'formulario_empleado': 'Formulario de Empleado',
      'planilla_masiva': 'Planilla Masiva',
      'api_externa': 'API Externa',
      'importacion': 'Importación de Datos',
      'recalculo': 'Recálculo Automático',
      'test': 'Pruebas/Testing'
    };
    
    return fuentes[origen] || 'Origen Desconocido';
  },

  /**
   * Obtener contexto adicional basado en el origen
   */
  obtenerContexto(origen) {
    const contextos = {
      'formulario_empleados_boton_generar': {
        descripcion: 'Cálculo iniciado desde el botón Generar del formulario de empleados',
        requiereValidacion: true,
        permiteCambios: true
      },
      'formulario_empleado': {
        descripcion: 'Cálculo individual desde formulario de empleado',
        requiereValidacion: true,
        permiteCambios: true
      },
      'planilla_masiva': {
        descripcion: 'Procesamiento masivo de planilla',
        requiereValidacion: false,
        permiteCambios: false
      },
      'api_externa': {
        descripcion: 'Llamada desde API externa',
        requiereValidacion: true,
        permiteCambios: false
      },
      'importacion': {
        descripcion: 'Importación de datos externos',
        requiereValidacion: true,
        permiteCambios: true
      },
      'recalculo': {
        descripcion: 'Recálculo automático del sistema',
        requiereValidacion: false,
        permiteCambios: false
      },
      'test': {
        descripcion: 'Ejecución de pruebas',
        requiereValidacion: false,
        permiteCambios: true
      }
    };
    
    return contextos[origen] || {
      descripcion: 'Contexto no definido',
      requiereValidacion: true,
      permiteCambios: true
    };
  }
};

export default calculosBoletaService; 